import {
  Button,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
} from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SubtaskStatusLabel } from '../../../../constants';
import { AppState } from '../../../../state/configureStore';
import {
  clearSubtask,
  clearSubtaskItemSection,
  fetchRelatedSubtaskListByTaskId,
  SubtaskState,
} from '../../../../state/subtask';
import { UserLetterAvatar } from '../../../components';
import { DateDisplay } from '../../../components/DateTimeDisplay';
import SubtaskActionMenu from './SubtaskActionMenu';
import SubtaskCreateDialog from './SubtaskCreateDialog';
import SubtaskEditDialog from './SubtaskEditDialog';
import useStyles from './subtaskList.style';
import history from '../../../../history';

interface SubtaskListProps {
  smTaskId: number;
}

const SubtaskList: React.FC<SubtaskListProps> = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryString = new URLSearchParams(location.search);
  const openModalWithSubtaskId = queryString.get('subtask');

  const { smTaskId } = props;
  const classes = useStyles();
  const { items: subtaskList } = useSelector<AppState, SubtaskState>(
    (state) => state.subtask,
  );

  const [openSubtaskCreateDialog, setOpenSubtaskCreateDialog] = useState(false);
  const [openSubtaskEditDialog, setOpenSubtaskEditDialog] = useState(false);
  const [editSubtaskId, setEditSubtaskId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchRelatedSubtaskListByTaskId(smTaskId));
  }, [dispatch, smTaskId]);

  useEffect(() => {
    return function cleanUp() {
      dispatch(clearSubtask());
    };
  }, [dispatch]);

  useEffect(() => {
    if (openModalWithSubtaskId !== null) {
      setEditSubtaskId(+openModalWithSubtaskId);
      setOpenSubtaskEditDialog(true);
    }
  }, [openModalWithSubtaskId]);

  const handleSetEditSubtaskId = (subtaskId: number) => {
    setEditSubtaskId(subtaskId);
    setOpenSubtaskEditDialog(true);
  };

  const getStatusColor = (status?: string) => {
    const s = (status || '').toUpperCase();
    const map: Record<string, string> = {
      PENDING: '#90A4AE',
      IN_PROGRESS: '#2196F3',
      DONE: '#4CAF50',
      CANCELLED: '#F44336',
    };
    return map[s] || '#9E9E9E';
  };

  return (
    <>
      {/* modernized header + table */}
      <Paper elevation={2} style={{ padding: 12, borderRadius: 16 }}>
        <CardContent style={{ padding: 2 }}>
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                dispatch(clearSubtaskItemSection());
                setOpenSubtaskCreateDialog(true);
              }}
              className={classes.createSubtaskButton}
            >
              <AddIcon className={classes.leftIcon} />
              New Subtask
            </Button>
          </Box>

          <Box className={classes.subtaskTableScroller}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 60 }}>No.</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Complete</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subtaskList.map((subtask, index) => {
                  const assigneeUsers =
                    subtask.smSubtaskAssignees?.length ?? 0 > 0
                      ? subtask.smSubtaskAssignees?.map(
                          (smSubtaskAssignee) => smSubtaskAssignee.user,
                        ) ?? []
                      : [];

                  const statusLabel = SubtaskStatusLabel[subtask.status ?? ''] || '';
                  const statusBg = getStatusColor(subtask.status);

                  return (
                    <TableRow key={subtask.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{subtask.subject}</Typography>
                        {subtask.description ? (
                          <Typography variant="body2" color="textSecondary">
                            {subtask.description}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{subtask.smSubtaskType?.name ?? ''}</TableCell>
                      <TableCell>
                        {assigneeUsers.length > 0 ? (
                          <UserLetterAvatar users={assigneeUsers} />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <DateDisplay value={subtask.dueDate} />
                      </TableCell>
                      <TableCell>
                        <DateDisplay value={subtask.completedDate} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel}
                          size="small"
                          style={{
                            backgroundColor: statusBg,
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <SubtaskActionMenu
                          subtask={subtask}
                          setEditSubtaskId={handleSetEditSubtaskId}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Paper>

      <SubtaskCreateDialog
        open={openSubtaskCreateDialog}
        handleClose={() => {
          setOpenSubtaskCreateDialog(false);
        }}
        subtaskId={null}
      />
      <SubtaskEditDialog
        open={openSubtaskEditDialog}
        handleClose={() => {
          dispatch(fetchRelatedSubtaskListByTaskId(smTaskId));
          setEditSubtaskId(null);
          setOpenSubtaskEditDialog(false);

          history.replace(history.location.pathname);
        }}
        subtaskId={editSubtaskId}
      />
    </>
  );
};

export default SubtaskList;
